/**
 * Utility for persisting queue tracking across page reloads
 */

interface QueueTrackingData {
  jobId: string;
  queueName: string;
  timestamp: number;
}

const STORAGE_PREFIX = 'queue-tracking';
const STALE_THRESHOLD = 60 * 60 * 1000; // 1 hour

/**
 * Generate storage key for queue tracking
 */
export function getQueueTrackingKey(identifier: string): string {
  return `${STORAGE_PREFIX}-${identifier}`;
}

/**
 * Save queue tracking data to localStorage
 */
export function saveQueueTracking(
  identifier: string,
  jobId: string,
  queueName: string
): void {
  if (typeof window === 'undefined') return;

  const data: QueueTrackingData = {
    jobId,
    queueName,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(getQueueTrackingKey(identifier), JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save queue tracking:', err);
  }
}

/**
 * Load queue tracking data from localStorage
 * Returns null if data is stale or invalid
 */
export function loadQueueTracking(
  identifier: string
): { jobId: string; queueName: string } | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(getQueueTrackingKey(identifier));
    if (!stored) return null;

    const data: QueueTrackingData = JSON.parse(stored);

    // Check if data is stale (older than threshold)
    if (data.timestamp && Date.now() - data.timestamp > STALE_THRESHOLD) {
      clearQueueTracking(identifier);
      return null;
    }

    if (!data.jobId || !data.queueName) return null;

    return {
      jobId: data.jobId,
      queueName: data.queueName
    };
  } catch (err) {
    console.error('Failed to load queue tracking:', err);
    clearQueueTracking(identifier);
    return null;
  }
}

/**
 * Clear queue tracking data from localStorage
 */
export function clearQueueTracking(identifier: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(getQueueTrackingKey(identifier));
  } catch (err) {
    console.error('Failed to clear queue tracking:', err);
  }
}

/**
 * Clean up all stale queue tracking data
 * Call this on app initialization or periodically
 */
export function cleanupStaleQueueTracking(): void {
  if (typeof window === 'undefined') return;

  try {
    const keys = Object.keys(localStorage);
    const queueKeys = keys.filter(key => key.startsWith(STORAGE_PREFIX));

    queueKeys.forEach(key => {
      const stored = localStorage.getItem(key);
      if (!stored) return;

      try {
        const data: QueueTrackingData = JSON.parse(stored);
        if (data.timestamp && Date.now() - data.timestamp > STALE_THRESHOLD) {
          localStorage.removeItem(key);
        }
      } catch {
        // Invalid data, remove it
        localStorage.removeItem(key);
      }
    });
  } catch (err) {
    console.error('Failed to cleanup stale queue tracking:', err);
  }
}
